import React from 'react';
import last from 'lodash/last';

import t from 'react-translate';
import { QuestionContext } from 'lecture_pages/services/question-set-context';
import { getAlphabetCharacter, removeDecimalForInteger } from 'quizzes/components/utils';
import useQuestionSetHelpers from 'quizzes/hooks/use-question-set-helpers';

type Props = {
  className?: string;
};

const CorrectAnswer = (props: Props) => {
  const { className } = props;

  const { responseOptions, currentQuestion } = React.useContext(QuestionContext);

  const {
    isMultipleChoiceQuestion,
    isShortAnswerQuestion,
    isNumberAnswerQuestion,
    isLikertAnswerQuestion,
    isMatchingQuestion,
  } = useQuestionSetHelpers();

  const getCorrectAnswers = () => {
    // Multiple choice question and Multiple choice multiple answer question
    if (isMultipleChoiceQuestion) {
      return responseOptions.map((option, index) => ({
        optionIdentifier: getAlphabetCharacter(index),
        option,
      })).filter(({ option }) => option.isCorrect).map(({ optionIdentifier }) => optionIdentifier);
    }

    // Short answer question, optionContent stores the short answer
    if (isShortAnswerQuestion) {
      // if no short answer is set
      if (!responseOptions[0].optionContent) {
        return [];
      }

      return responseOptions
        .filter((option) => option.optionContent !== '')
        .map((option) => option.optionContent);
    }

    /**
     * number answer question and likert answer question shares same fields
     * for correct answers, ie topOfRange and bottomOfRange
     */
    if (isNumberAnswerQuestion || isLikertAnswerQuestion) {
      return [
        removeDecimalForInteger(responseOptions[0].bottomOfRange),
        removeDecimalForInteger(responseOptions[0].topOfRange),
      ];
    }
    return [];
  };

  const correctAnswers = getCorrectAnswers();

  const renderCorrectAnswers = () => {
    const lastCorrectAnswer = last(correctAnswers);
    const before = correctAnswers.slice(0, correctAnswers.length - 1);

    if (isMultipleChoiceQuestion) {
      const isAdminNotSettedAnyCorrectAnswer = !currentQuestion?.numberOfCorrectOptions;

      if (isAdminNotSettedAnyCorrectAnswer) {
        return `${before.join(', ')}${before.length ? t.JOIN.MANY.OR() : ''}${lastCorrectAnswer}`;
      }

      return correctAnswers.join(', ');
    }
    if (isShortAnswerQuestion) {
      if (!correctAnswers.length) {
        return t.QUIZZES.NO_CORRECT_ANSWER_DEFINED();
      }
      return `${before.join(', ')}${before.length ? t.JOIN.MANY.OR() : ''}${lastCorrectAnswer}`;
    }
    if (isNumberAnswerQuestion || isLikertAnswerQuestion) {
      if (!(correctAnswers[0] || correctAnswers[1])) {
        return t.QUIZZES.NO_CORRECT_ANSWER_DEFINED();
      }
      if (correctAnswers[0] === correctAnswers[1]) {
        return correctAnswers[0];
      }
      return t.QUIZZES.CORRECT_ANSWER_RANGE(correctAnswers[0], correctAnswers[1]);
    }
    if (isMatchingQuestion) {
      return t.QUIZZES.MATCHING_QUESTION_SEE_CORRECT_ANSWER();
    }
    return null;
  };

  return (
    <div className={`text-large-body bold text-success ${className ?? ''}`}>
      {t.QUIZZES.CORRECT_ANSWER()}
      {' '}
      {renderCorrectAnswers()}
    </div>
  );
};

export default CorrectAnswer;
